'use client';

import { useEffect, useRef } from 'react';
import { trackStatistic } from '@/lib/utils/statisticTracker';

/**
 * Fires a statistic hit once the data is ready and re-fires only when the
 * tracked target actually changes.
 *
 * - Runs on mount, so hard refresh / direct load / deep-link are counted.
 * - `dedupKey` lets a single mounted page (e.g. a dynamic `[id]` route that
 *   re-renders instead of remounting) re-count when the selected item changes.
 * - Ref-based dedup keeps it safe against re-renders and React StrictMode's
 *   double-invoked effects.
 */
export const useTrackStatistic = (
  key: string,
  itemId?: number | string | null,
  dedupKey?: number | string | null
) => {
  const lastSignature = useRef<string | null>(null);

  useEffect(() => {
    if (itemId === undefined || itemId === null) return;

    const signature = `${key}|${itemId}|${dedupKey ?? ''}`;
    if (lastSignature.current === signature) return;
    lastSignature.current = signature;

    void trackStatistic(key, itemId);
  }, [key, itemId, dedupKey]);
};
